import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';

import { GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals } from 'src/api/queries/__generated__/GetSellerInputQuery';
import { PartnershipReferral } from 'src/declarations';
import { Markets } from 'src/helpers/markets';
import { getIsInPartnershipExpansionDenial } from 'src/lib/flow/sellerInputUtils';

import { getAnswersFromLocalStorage } from '../../helpers/answers';
import { isAgent } from '../../lib/flow/pageHelpers';
import { sellerFlowPagePath } from '../../routes/pathBuilders';
import { RootState } from '../index';
import { ReduxState } from '../reducers';
import { getInApp, getLeadToken } from '../router/selectors';
import { AnswerPrefill } from '../sellerInput';
import { AnswerSet } from './AnswerSet';

const sellerFlowName = (state: RootState) => state.flow.name;
const productOfferings = (state: RootState) => state.sellerInput.productOfferings ?? [];
export const leadState = (state: RootState) => state.sellerInput.leadState ?? '';

const eligibleProducts = createSelector(
  [productOfferings, leadState],
  (productOfferings, leadState) => {
    const eligibleProducts = productOfferings
      .filter((productOffering) => !productOffering.denied)
      .map((productOffering) => productOffering.product);
    // HACK: Leads created in the seller app use Web and not Reception, meaning they'll never
    // have any product offerings at all.
    // We should remove this once the mobile app seller flow is moved onto Reception.
    if (productOfferings.length === 0 && leadState !== 'denied') {
      eligibleProducts.push('opendoor_direct');
    }
    return eligibleProducts;
  },
);

// Updated selectors below pull from our new state slices
export const address = (state: RootState) => state.sellerInput.address;

// TODO: see if this can be a useSelector or not a reselect
export const addressUUID = createSelector(address, (address) => address?.uuid ?? '');

export const channel = (state: RootState) => state.sellerInput.channel ?? '';
export const denialCode = (state: RootState) => state.sellerInput.denialCode;
export const denialReason = (state: RootState) => state.sellerInput.denialReason;
export const denialPartnerIdentifier = (state: RootState) =>
  state.sellerInput.denialPartnerIdentifier ?? '';
export const lastCompletedSlug = (state: RootState) => state.sellerInput.lastCompletedSlug ?? '';
export const lastListing = (state: RootState) => state.sellerInput.lastListing;
export const lastListingPhotos = (state: RootState) => state.sellerInput.lastListingPhotos ?? [];

export const leadToken = (state: RootState) => state.sellerInput.leadToken ?? '';
export const market = (state: RootState) => state.sellerInput.market ?? '';
export const odDirectDenialReasons = (state: RootState) => state.sellerInput.odDirectDenialReasons;

export const areAllProductsDenied = createSelector(
  [eligibleProducts],
  (eligibleProducts) => eligibleProducts.length === 0,
);

export const getPageSetName = (state: RootState) => state.flow_ng.pageSetName;

export const offerPresent = (state: RootState) => state.sellerFlow.getIn(['offerPresent']);

export const isAddressEntryConfirmationTreatment = (state: RootState) => {
  state.experimentFlags.addressEntryConfirmation == 'treatment';
};

export const isInListWithCertaintyTreatment = (state: RootState) =>
  state.featureFlags.isInListWithCertaintyTreatment;

export const isInExclusiveOffersTreatment = (state: RootState) => {
  return (
    state.sellerInput.productOfferings.filter(
      (productOffering) =>
        !productOffering.denied && productOffering.product === 'sell_with_exclusives',
    ).length > 0 || state.flow_ng.pageSetName == 'exclusives'
  );
};

// No agents or partners should see the chatbot reengagement experiment
// wrap notAgentOrPartner so it's obvious that it's specific to this test
export const isEligibleForChatbotFullScaleTest = (state: RootState) => {
  return notAgentOrPartner(state) && !isInExclusiveOffersTreatment(state);
};

// Eligiblity criteria:
// - Not an agent or partner
// - Not in the exclusive offers treatment
// - Not in list with certainty treatment
export const isEligibleForChatbotHomePhotosRequestTest = (state: RootState) => {
  return (
    notAgentOrPartner(state) &&
    !isInExclusiveOffersTreatment(state) &&
    !isInListWithCertaintyTreatment(state)
  );
};

export const isInExclusiveMarket = (state: RootState) =>
  [Markets.AUSTIN, Markets.DALLAS, Markets.HOUSTON].includes(state.sellerInput.market as Markets);

export const answersErrors = (state: ReduxState) => state.sellerFlow.getIn(['answersErrors']);
export const answersIsSubmitting = (state: ReduxState) =>
  state.sellerFlow.getIn(['answersIsSubmitting']);
export const changedAnswers = (state: RootState) => state.sellerInput.changedAnswers;

export const flow = (state: RootState) => state.flow;

const savedAnswers = (state: RootState) => state.sellerInput.savedAnswers ?? {};

const answerPrefills = (state: RootState): Record<string, AnswerPrefill> =>
  state.sellerInput.answerPrefills ?? {};

export const getAnswers = createSelector(
  [answerPrefills, savedAnswers, getLeadToken, changedAnswers],
  (answerPrefills, savedAnswers, leadToken, changedAnswers): AnswerSet => {
    //FIXME answerPrefills - these need to be validated that they are within range of the questions we have

    const prefills = Object.entries(answerPrefills).reduce((all, [key, valueObj]) => {
      all[key] = valueObj.value;
      return all;
    }, {});

    return {
      ...prefills,
      ...savedAnswers,
      ...getAnswersFromLocalStorage(leadToken),
      ...changedAnswers,
    };
  },
);

export const nonSeriousSeller = createSelector(
  getAnswers,
  (answers) => answers['seller.sale_timeline'] === 'Just Browsing',
);

export const submittedByAgent = createSelector([getAnswers, channel], (answers, channel) =>
  isAgent(answers, channel),
);

const partnerships = (state: RootState) => state.sellerInput.partnerships ?? [];

export const isInPartnershipExpansionDenial = createSelector(
  [productOfferings, denialPartnerIdentifier],
  (productOfferings, denialPartnerIdentifier) => {
    return getIsInPartnershipExpansionDenial(productOfferings, denialPartnerIdentifier);
  },
);

export const isPartnerFlow = createSelector(
  [submittedByAgent, partnerships, isInPartnershipExpansionDenial, getAnswers, sellerFlowName],
  (isAgent, partnerships, isInPartnershipExpansionDenial, answers, sellerFlowName) => {
    const submittedBySelf = !(isAgent || answers['seller.relation_to_owner'] === 'other');
    const homeBuilderOrOREReferral = partnerships.some(
      (referral) => referral.type === 'home_builder' || referral.type === 'online_real_estate',
    );
    const workingWithHomeBuilder = answers['seller.working_with_home_builder'] === true;
    const selfReportListed = answers['seller.listed'] === true;
    if (
      (submittedBySelf &&
        !homeBuilderOrOREReferral &&
        !workingWithHomeBuilder &&
        !selfReportListed &&
        sellerFlowName === 'partner') ||
      isInPartnershipExpansionDenial
    ) {
      return true;
    }
    return false;
  },
);

const getHomeBuilderIdentifier = (partnerships: Array<PartnershipReferral>): string | undefined => {
  const homebuilder = partnerships?.find((referral) => referral.type === 'home_builder');
  if (!homebuilder || !homebuilder.identifier) {
    return;
  }
  return homebuilder.identifier;
};

export const isHomeBuilderLead = (
  partnerships: Array<
    | GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals
    | PartnershipReferral
  >,
  answers: AnswerSet,
) => {
  const homebuilderIdentifier = getHomeBuilderIdentifier(partnerships);
  // there's a race condition when grabbing the partnerships data, so we should also check if
  // answered 'yes' to the homebuilder questions in the flow. That way smoke tests and fast users
  // will still get the right experience
  return homebuilderIdentifier || answers['seller.home_builder'];
};

const getPreviousPage = (state: RootState) => state.ui.previousPage;

export const getPreviousPath = createSelector(
  [getPreviousPage, getLeadToken, getInApp],
  (previousPage, leadToken, inApp): string | undefined => {
    if (!previousPage?.slug) {
      return; // undefined hides back button
    }

    return sellerFlowPagePath({
      token: leadToken,
      slug: previousPage.slug,
      inApp,
    });
  },
);

export const getCurrentPageError = createSelector([], () => {
  //FIXME - pretty sure this is not used but it is still referenced
  return undefined;
});

export const isAddressUnitNumberMissing = createSelector(
  address,
  (address) => address?.isMissingUnitNumber && isEmpty(address?.unitNumber),
);

// TODO - this seems to not be set anymore
export const isAddressUnitNumberWrong = createSelector(
  address,
  (address) => address?.isInvalidUnitNumber && !isEmpty(address?.unitNumber),
);

export const notAgentOrPartner = createSelector(
  [partnerships, getAnswers, channel],
  (partnerships, answers, channel) => {
    const homeBuilderOrOREReferral = partnerships.some(
      (referral) => referral.type === 'home_builder' || referral.type === 'online_real_estate',
    );
    return (
      !isAgent(answers, channel) &&
      !homeBuilderOrOREReferral &&
      !isHomeBuilderLead(partnerships, answers)
    );
  },
);

export const hasPhoneNumber = createSelector([getAnswers], (answers) => {
  return !!answers['seller.phone_number'];
});

export const isPhoneNumberRequired = createSelector(
  [submittedByAgent, market, isInExclusiveOffersTreatment],
  (submittedByAgent, market, isInExclusiveOffersTreatment) => {
    const isInSellWithCertaintyMarket = ['charlotte'].includes(market);
    if (isInSellWithCertaintyMarket || submittedByAgent || isInExclusiveOffersTreatment) {
      return true;
    }
    return false;
  },
);

export const progressPercentage = (state: RootState) => {
  const progress = state.ui.progress;
  return Math.round(100 * (progress.current / (progress.total + 1)));
};
